// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	classifyComposerDraftChange,
	composerDraftChangeWindow,
} from '@app/features/holiday/components/ComposerHolidayPeekDraft';
import {
	resolveComposerHolidayPeekMachineInput,
	resolveComposerHolidayPeekPack,
} from '@app/features/holiday/components/ComposerHolidayPeekResolvers';
import {
	type ComposerHolidayPeekSnapshot,
	type ComposerHolidayPeekView,
	composerHolidayPeekViewEquals,
	createComposerHolidayPeekSnapshot,
	selectComposerHolidayPeekView,
	transitionComposerHolidayPeekSnapshot,
} from '@app/features/holiday/components/ComposerHolidayPeekStateMachine';
import {makeAutoObservable, observableRef} from 'mobx';

class ComposerHolidayPeekSessionState {
	snapshot: ComposerHolidayPeekSnapshot | null = null;
	model: ComposerHolidayPeekView | null = null;
	private lastDraftByChannel = new Map<string, string>();
	private timerId: ReturnType<typeof setTimeout> | null = null;
	private snapshotPackId: string | null = null;

	constructor() {
		makeAutoObservable<this, 'lastDraftByChannel' | 'timerId' | 'snapshotPackId'>(
			this,
			{
				snapshot: observableRef,
				model: observableRef,
				lastDraftByChannel: false,
				timerId: false,
				snapshotPackId: false,
			},
			{autoBind: true},
		);
	}

	private ensureSnapshot(): ComposerHolidayPeekSnapshot | null {
		const pack = resolveComposerHolidayPeekPack();
		if (pack == null) return null;
		// A snapshot holds one pack's stage ids. If the resolved pack changed under us — a season
		// starting or ending mid-compose, or a preference toggle — rebuild rather than render ids
		// the new pack does not have.
		if (this.snapshot != null && this.snapshotPackId === pack.id) return this.snapshot;
		if (this.snapshot != null) this.dropSnapshot();
		this.snapshot = createComposerHolidayPeekSnapshot(resolveComposerHolidayPeekMachineInput(pack));
		this.snapshotPackId = pack.id;
		return this.snapshot;
	}

	noteDraftChange(channelId: string, value: string, now: number, roll: number): void {
		const previous = this.lastDraftByChannel.get(channelId);
		if (previous === undefined) {
			this.lastDraftByChannel.set(channelId, value);
			if (value.length > 0) this.maybeInterrupt(channelId, value, value, now);
			return;
		}
		if (previous === value) return;
		this.lastDraftByChannel.set(channelId, value);
		if (
			this.snapshot != null &&
			this.snapshot.context.channelId != null &&
			this.snapshot.context.channelId !== channelId
		) {
			this.apply({type: 'peek.composeReset', channelId: this.snapshot.context.channelId, now});
			this.dropSnapshot();
		}
		if (value.length === 0) {
			this.apply({type: 'peek.composerCleared', channelId, now});
			return;
		}
		const change = classifyComposerDraftChange(previous, value);
		if (change == null) {
			this.maybeInterrupt(channelId, value, value, now);
			return;
		}
		const changeWindow = composerDraftChangeWindow(previous, value);
		if (change.type === 'delete') {
			this.apply({type: 'peek.composerDelete', channelId, now});
			this.maybeInterrupt(channelId, value, changeWindow, now);
			return;
		}
		this.apply({
			type: 'peek.composerInsert',
			channelId,
			now,
			roll,
			kind: change.kind,
			chars: change.chars,
		});
		this.maybeInterrupt(channelId, value, changeWindow, now);
	}

	noteIdleElapsed(channelId: string, now: number): void {
		this.apply({type: 'peek.idleElapsed', channelId, now});
	}

	noteMotionElapsed(channelId: string, now: number, roll: number): void {
		this.apply({type: 'peek.motionElapsed', channelId, now, roll});
	}

	reset(channelId: string, now: number): void {
		this.lastDraftByChannel.delete(channelId);
		this.apply({type: 'peek.composeReset', channelId, now});
	}

	/**
	 * New matches can only be completed by the edit, so trigger on `changeWindow`. A showing interrupt
	 * scans the whole draft instead, since it hides once the phrase is gone from anywhere in it.
	 */
	private maybeInterrupt(channelId: string, value: string, changeWindow: string, now: number): void {
		const pack = resolveComposerHolidayPeekPack();
		const showingInterrupt = this.snapshot?.context.interruptStageId != null;
		const stageId = pack?.matchInterrupt?.(showingInterrupt ? value : changeWindow) ?? null;
		if (stageId != null) {
			this.apply({type: 'peek.interrupt', channelId, now, stageId});
			return;
		}
		if (showingInterrupt) {
			this.apply({type: 'peek.interruptDismissed', channelId, now});
		}
	}

	private apply(event: Parameters<typeof transitionComposerHolidayPeekSnapshot>[1]): void {
		const snapshot = event.type === 'peek.composeReset' && this.snapshot == null ? null : this.ensureSnapshot();
		if (snapshot == null) return;
		this.snapshot = transitionComposerHolidayPeekSnapshot(snapshot, event);
		this.publishView();
		this.syncTimers();
	}

	private dropSnapshot(): void {
		this.clearTimer();
		this.snapshot = null;
		this.snapshotPackId = null;
		if (this.model != null) this.model = null;
	}

	private publishView(): void {
		const next = this.snapshot == null ? null : selectComposerHolidayPeekView(this.snapshot);
		if (composerHolidayPeekViewEquals(this.model, next)) return;
		this.model = next;
	}

	private clearTimer(): void {
		if (this.timerId == null) return;
		clearTimeout(this.timerId);
		this.timerId = null;
	}

	private syncTimers(): void {
		this.clearTimer();
		const context = this.snapshot?.context;
		if (context == null || context.channelId == null) return;
		const channelId = context.channelId;
		const times = [context.hideAt, context.motionEndsAt].filter((time): time is number => time != null);
		if (times.length === 0) return;
		const delay = Math.max(0, Math.min(...times) - Date.now());
		this.timerId = setTimeout(() => {
			this.timerId = null;
			const latest = this.snapshot?.context;
			if (latest == null || latest.channelId !== channelId) return;
			const now = Date.now();
			if (latest.motionEndsAt != null && now >= latest.motionEndsAt) {
				this.noteMotionElapsed(channelId, now, Math.random());
				return;
			}
			if (latest.motion === 'hold' && latest.hideAt != null && now >= latest.hideAt) {
				this.noteIdleElapsed(channelId, now);
			}
		}, delay);
	}
}

const ComposerHolidayPeekSession = new ComposerHolidayPeekSessionState();

export default ComposerHolidayPeekSession;
